import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import jobService from '../../utils/jobService';
import { getJobDetailsUrl, getJobApplicationUrl } from '../../utils/urlHelpers';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import VAHeaderNav from '../../components/navigation/VAHeaderNav';
import JobCard from '../job-search/components/JobCard';

const SavedJobs = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();
  
  const [bookmarkedJobs, setBookmarkedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  const loadBookmarkedJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await jobService.getUserBookmarks(user.id);

      if (!result?.success) {
        setError(result?.error || 'Failed to load saved jobs');
        return;
      }

      // Transform the data to match the job card component format
      const formattedJobs = result.data.map(bookmark => ({
        ...bookmark.job,
        isBookmarked: true // All jobs here are bookmarked
      }));
      
      setBookmarkedJobs(formattedJobs);
    } catch (error) {
      setError('Something went wrong while loading saved jobs');
      console.error('Load bookmarked jobs error:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);
  
  useEffect(() => {
    if (!authLoading && user) {
      loadBookmarkedJobs();
    }
  }, [authLoading, user, loadBookmarkedJobs]);

  const handleJobClick = (job) => {
    navigate(getJobDetailsUrl(job));
  };

  const handleApply = (job) => {
    navigate(getJobApplicationUrl(job));
  };

  const handleBookmark = async (jobId) => {
    try {
      const result = await jobService.toggleBookmark(user.id, jobId);
      
      if (result?.success && !result.bookmarked) {
        // Remove the job from the list when unbookmarked
        setBookmarkedJobs(prev => prev.filter(job => job.id !== jobId));
      }
    } catch (error) {
      console.error('Bookmark error:', error);
    }
  };

  // Show loading state for initial load
  if (authLoading || (loading && bookmarkedJobs.length === 0)) {
    return (
      <div className="min-h-screen bg-background">
        <VAHeaderNav 
          title="Saved Jobs"
          showBackButton={true}
          user={user}
          userProfile={userProfile}
        />
        <main className="pt-4">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
                <p className="text-text-secondary">Loading saved jobs...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <VAHeaderNav 
        title="Saved Jobs"
        showBackButton={true}
        user={user}
        userProfile={userProfile}
      />
      
    
      {/* Main Content */}
      <main className="pt-4">
        <div className="content-container mx-auto px-4 py-8">
          {/* Results Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-text-primary mb-1">
                Saved Jobs
              </h1>
              <p className="text-text-secondary">
                {bookmarkedJobs.length} job{bookmarkedJobs.length !== 1 ? 's' : ''} bookmarked
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center border border-border rounded-lg overflow-hidden">
                <button
                  className={`p-2 ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-surface text-text-secondary hover:bg-background'}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid view"
                >
                  <Icon name="Grid" size={18} />
                </button>
                <button
                  className={`p-2 ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-surface text-text-secondary hover:bg-background'}`}
                  onClick={() => setViewMode('list')}
                  title="List view"
                >
                  <Icon name="List" size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <Icon name="AlertCircle" size={20} className="text-error mr-3" />
                <div>
                  <p className="text-error font-medium">Error loading saved jobs</p>
                  <p className="text-error-600 text-sm">{error}</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={loadBookmarkedJobs}
                className="mt-3"
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Jobs Grid or List */}
          {bookmarkedJobs.length > 0 ? (
            <div className="space-y-4">
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6">
                  {bookmarkedJobs.map((job) => (
                    <JobCard
                      key={job.id}
                      job={job}
                      onViewDetails={handleJobClick}
                      onApply={handleApply}
                      onBookmark={handleBookmark}
                      userProfile={userProfile}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {bookmarkedJobs.map((job) => (
                    <div 
                      key={job.id}
                      className="bg-surface rounded-lg border border-border hover:border-primary overflow-hidden cursor-pointer"
                      onClick={() => handleJobClick(job)}
                    >
                      <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-3">
                            {job.employer?.profile_picture ? (
                              <img
                                src={job.employer.profile_picture}
                                alt={job.employer.company_name || 'Company logo'}
                                className="h-12 w-12 rounded object-cover"
                              />
                            ) : (
                              <div className="h-12 w-12 bg-background rounded flex items-center justify-center">
                                <Icon name="Briefcase" size={24} className="text-text-muted" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg font-semibold text-text-primary truncate">{job.title}</h3>
                              <p className="text-text-secondary truncate">
                                {job.employer?.company_name || 'Unknown company'}
                              </p>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                                <span className="flex items-center text-sm text-text-muted">
                                  <Icon name="MapPin" size={14} className="mr-1" /> 
                                  {job.is_remote ? 'Remote' : job.location}
                                </span>
                                {job.job_type && (
                                  <span className="flex items-center text-sm text-text-muted">
                                    <Icon name="Clock" size={14} className="mr-1" /> 
                                    {job.job_type.replace('_', ' ')}
                                  </span>
                                )}
                                {job.budget_min && job.budget_max && (
                                  <span className="flex items-center text-sm text-text-muted">
                                    <Icon name="DollarSign" size={14} className="mr-1" /> 
                                    ${job.budget_min.toLocaleString()} - ${job.budget_max.toLocaleString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {job.required_skills && job.required_skills.length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-2">
                              {job.required_skills.slice(0, 3).map((skill) => (
                                <span 
                                  key={skill} 
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700"
                                >
                                  {typeof skill === 'object' && skill !== null ? (skill.name || skill.skill || 'Unknown') : skill}
                                </span>
                              ))}
                              {job.required_skills.length > 3 && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-background text-text-secondary">
                                  +{job.required_skills.length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex md:flex-col items-center gap-3 mt-4 md:mt-0">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApply(job);
                            }}
                          >
                            Apply Now
                          </Button>
                          <button
                            className="p-2 rounded-full hover:bg-background"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBookmark(job.id);
                            }}
                            title="Remove bookmark"
                          >
                            <Icon 
                              name="Bookmark" 
                              size={20} 
                              className="text-primary" 
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : !loading && (
            // Empty State
            <div className="text-center py-12">
              <Icon name="Bookmark" size={64} className="text-text-muted mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-text-primary mb-2">
                No saved jobs
              </h3>
              <p className="text-text-secondary mb-6 max-w-md mx-auto">
                You haven't bookmarked any jobs yet. Browse jobs and save the ones you're interested in!
              </p>
              <Button
                variant="primary"
                onClick={() => navigate('/job-search')}
              >
                Browse Jobs
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SavedJobs;


